import http from 'utlis/http';

const designAuditApi = process.env.REACT_APP_DESIGN_AUDIT_BASE_URL;

const CAPTURE_STATUS = {
  BLOCKED: 'blocked',
  FAILED: 'failed',
};

/**
 * POST {REACT_APP_DESIGN_AUDIT_BASE_URL}/capture_landing_page  (application/json)
 *
 * Captures a full-page screenshot of a public landing page and uploads it (public-read) to S3.
 * Synchronous: typically 10-35 s, up to ~2 min.
 *
 * Request: { url }
 * Responses:
 *   200 — { status: 'success', capture_id, screenshot: { key, url }, meta: { key, url }, capture: { final_url, title, ... } }
 *   400 — { status: 'error', message }                                  invalid / non-public URL
 *   422 — { status: 'error', capture_status: 'blocked' | 'failed', message, capture }
 *   502 — { status: 'error', message, capture_id }                      S3 upload failed
 *
 * @param {string} url - Normalized http(s) URL
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ screenshotUrl: string, finalUrl: string, title: string, captureId: string }>}
 */
export const captureLandingPage = async (url, signal = null) => {
  const { data } = await http.post(
    `${designAuditApi}/capture_landing_page`,
    { url },
    {
      headers: {
        'X-BACKEND-URL': process.env.REACT_APP_X_BACKEND_URL,
      },
      ...(signal ? { signal } : {}),
    }
  );

  if (data?.status !== 'success' || !data?.screenshot?.url) {
    throw new Error(data?.message || 'Landing page capture failed');
  }

  return {
    screenshotUrl: data.screenshot.url,
    finalUrl: data.capture?.final_url || url,
    title: data.capture?.title || '',
    captureId: data.capture_id,
  };
};

/**
 * Map a capture failure to user-facing copy. Raw server messages (e.g. `HTTP 403; marker
 * "Attention Required"`) are diagnostic, not meant for users, so they are never surfaced.
 */
export const getCaptureErrorMessage = error => {
  const httpStatus = error?.response?.status;
  const captureStatus = error?.response?.data?.capture_status;

  if (httpStatus === 400) {
    return 'Enter a valid, publicly accessible website URL.';
  }

  if (httpStatus === 422 && captureStatus === CAPTURE_STATUS.BLOCKED) {
    return 'This website blocked our capture. Try a different website, or upload screenshots from product library instead.';
  }

  if (httpStatus === 422 && captureStatus === CAPTURE_STATUS.FAILED) {
    return 'We couldn\'t load this page. Check the URL and try again.';
  }

  return 'Something went wrong capturing this page. Please try again.';
};
